import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Modal, Alert, Row, Col } from "react-bootstrap";
import {
  createSubject,
  updateSubject,
  clearError,
} from "../../redux/slices/subjectSlice";
import { fetchClasses } from "../../redux/slices/classSlice";
import { toast } from "react-toastify";

const SubjectModal = ({ show, onHide, subject = null }) => {
  const dispatch = useDispatch();
  const isEditMode = !!subject;

  const { isCreating, isUpdating, error, subjects = [] } = useSelector((state) => state.subjects);
  const { classes = [] } = useSelector((state) => state.classes);

  const [formData, setFormData] = useState({
    class_id: "",
    subject_name: "",
  });

  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (show) {
      if (classes.length === 0) {
        dispatch(fetchClasses());
      }
    }
  }, [show, dispatch, classes.length]);

  useEffect(() => {
    if (show) {
      if (isEditMode && subject) {
        setFormData({
          class_id: subject.class_id || "",
          subject_name: subject.subject_name || "",
        });
      } else {
        setFormData({
          class_id: "",
          subject_name: "",
        });
      }
      setValidationErrors({});
    }
  }, [show, isEditMode, subject]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    if (name === "subject_name") {
      finalValue = value.replace(/[^a-zA-Z\s]/g, "");
    }
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.class_id) {
      errors.class_id = "Please select a class";
    }
    const subjectName = formData.subject_name.trim();
    if (!subjectName) {
      errors.subject_name = "Subject name is required";
    } else if (!/^[a-zA-Z\s]+$/.test(subjectName)) {
      errors.subject_name = "Subject name should only contain letters and spaces";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;



    const submitData = {
      class_id: formData.class_id,
      subject_name: formData.subject_name.trim(),
    };

    try {
      if (isEditMode) {
        await dispatch(updateSubject({ id: subject.id, subjectData: submitData })).unwrap();
      } else {
        await dispatch(createSubject(submitData)).unwrap();
      }
      onHide();
    } catch (err) {
      console.error("Subject operation failed:", err);
      toast.error(err || "Something went wrong");
    }
  };

  const isSubmitting = isCreating || isUpdating;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? "Update Subject" : "Create New Subject"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => dispatch(clearError())}>
              {error}
            </Alert>
          )}

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Select Class <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="class_id"
                  value={formData.class_id}
                  onChange={handleInputChange}
                  isInvalid={!!validationErrors.class_id}
                  disabled={isSubmitting}
                >
                  <option value="">Select Class</option>
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.class_name} ({cls.board_name})
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {validationErrors.class_id}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Subject Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="subject_name"
                  placeholder="Enter subject name"
                  value={formData.subject_name}
                  onChange={handleInputChange}
                  isInvalid={!!validationErrors.subject_name}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.subject_name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn-outline-danger-custom"
            onClick={onHide}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="btn-outline-success"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Processing..."
              : isEditMode
              ? "Update Subject"
              : "Create Subject"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SubjectModal;
